import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Layout, 
  Button, 
  Avatar, 
  Typography,
  Drawer
} from 'antd';
import { MenuOutlined, HomeOutlined } from '@ant-design/icons';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Header from '../../../components/Common/Layout/Header';
import PanelLegalLinks from '../../../components/Common/PanelLegalLinks';
import { apiService } from '../../../services/api';
import { mentorProfileQueryKeys } from '../../../lib/queryClient';
import styles from './MentorPanel.module.css';
import { 
  UserOutlined, 
  CrownOutlined,
  BookOutlined,
  ProjectOutlined,
  SettingOutlined,
  StarOutlined,
  CalendarOutlined,
  LaptopOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  FundOutlined,
  LogoutOutlined
} from '@ant-design/icons';
import ProfileTab from './ProfileTab';
import EducationTab from './EducationTab';
import ProjectsTab from './ProjectsTab';
import SkillsTab from './SkillsTab';
import WorkExperienceTab from './WorkExperienceTab';
import CertificateTab from './CertificateTab';
import AvailabilityTab from './AvailabilityTab';
import BootcampsTab from './BootcampsTab';
import ServicesTab from './ServicesTab';
import AppointmentsTab from './AppointmentsTab';
import JobApplicationsTab from '../../Mentee/Panel/JobApplicationsTab';
import InterviewsTab from '../../Mentee/Panel/InterviewsTab';
import ReferenceRequestsTab from './ReferenceRequestsTab';
import MenteesTab from './MenteesTab';
import ReferanslarTab from './ReferanslarTab';
import AdvertisementTab from './AdvertisementTab';
import SubscriptionTab from './SubscriptionTab';
import SettingsTab from './SettingsTab';
import SubscriptionProtectedRoute from '../../../components/Common/SubscriptionProtectedRoute';

const { Sider, Content } = Layout;
const { Title, Text } = Typography;

const MentorPanel: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedMenu, setSelectedMenu] = useState('profile');
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
    setMobileMenuVisible(false);
  };

  const { data: profileData } = useQuery({
    queryKey: mentorProfileQueryKeys.profile,
    queryFn: async () => {
      const response = await apiService.getMentorProfile();
      if (!response.success) throw new Error(response.message);
      return response.data;
    },
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && ['subscription', 'job-applications', 'interviews', 'appointments', 'bootcamps', 'profile', 'referanslar', 'education', 'projects', 'skills', 'work', 'certificates', 'settings', 'availability', 'services', 'mentees', 'advertisements'].includes(tab)) {
      setSelectedMenu(tab);
      if (tab !== 'settings' && tab !== 'job-applications' && tab !== 'interviews') {
        const next = new URLSearchParams(searchParams);
        next.delete('tab');
        setSearchParams(next, { replace: true });
      }
    }
  }, [searchParams, setSearchParams]);

  const menuItems = [
    { key: 'profile', label: 'Profil Bilgileri', icon: <UserOutlined /> },
    { key: 'subscription', label: 'Abonelik Bilgileri', icon: <CrownOutlined /> },
    { key: 'settings', label: 'Güvenlik Ayarları', icon: <SettingOutlined /> },
    { key: 'education', label: 'Eğitim Bilgileri', icon: <BookOutlined /> },
    { key: 'projects', label: 'Projeler', icon: <ProjectOutlined /> },
    { key: 'skills', label: 'Yetkinlikler', icon: <SettingOutlined /> },
    { key: 'work', label: 'İş Hayatı', icon: <LaptopOutlined /> },
    { key: 'certificates', label: 'Sertifikalar', icon: <FileTextOutlined /> },
    { key: 'job-applications', label: 'İş Başvurularım', icon: <FileTextOutlined /> },
    { key: 'interviews', label: 'Mülakatlarım', icon: <CalendarOutlined /> },
    { key: 'services', label: 'Hizmetlerim', icon: <StarOutlined /> },
    { key: 'bootcamps', label: 'Bootcampler', icon: <CrownOutlined /> },
    { key: 'mentees', label: 'Menteelerim', icon: <UserOutlined /> },
    { key: 'referanslar', label: 'Referanslar', icon: <StarOutlined /> },
    { key: 'advertisements', label: 'Reklam Yönetimi', icon: <FundOutlined /> },
    { key: 'availability', label: 'Müsaitlik Takvimi', icon: <CalendarOutlined /> },
    { key: 'appointments', label: 'Randevular', icon: <ClockCircleOutlined /> }
  ];

  const handleMenuClick = (key: string) => {
    setSelectedMenu(key);
    setMobileMenuVisible(false);
  };

  const renderSidebar = () => (
    <>
      <div className="panel-sidebar-brand">
        <Avatar
          size={64}
          style={{ backgroundColor: 'var(--primary-600)', color: '#fff', fontSize: '20px', fontWeight: 'bold' }}
          src={profileData?.profileImage ? `data:image/jpeg;base64,${profileData.profileImage}` : undefined}
        >
          {profileData ? `${profileData.firstName.charAt(0)}${profileData.lastName.charAt(0)}` : 'AY'}
        </Avatar>
        <h2 className="panel-user-name">{profileData ? `${profileData.firstName} ${profileData.lastName}` : 'Yükleniyor...'}</h2>
        <span className="panel-user-role"><UserOutlined style={{ marginRight: 4 }} /> Mentor</span>
        {(profileData?.email || user?.email) && (
          <span className="panel-user-email">{profileData?.email || user?.email}</span>
        )}
      </div>
      <nav className="panel-nav">
        {menuItems.map((item) => (
          <button
            type="button"
            key={item.key}
            className={`panel-nav-item ${selectedMenu === item.key ? 'panel-nav-item--active' : ''}`}
            onClick={() => handleMenuClick(item.key)}
          >
            <span className="panel-nav-icon">{item.icon}</span>
            <span>{item.label}</span>
          </button>
        ))}
      </nav>
      <div className="panel-sidebar-footer">
        <PanelLegalLinks />
        <button type="button" className="panel-logout" onClick={handleLogout}>
          <LogoutOutlined /> Çıkış Yap
        </button>
      </div>
    </>
  );

  return (
    <Layout className={styles.profileContainer}>
      <Header />

      {/* Mobil: sticky panel header – menü ve anasayfa (Admin/Company ile aynı) */}
      <header className={styles.panelHeaderMobile}>
        <div className="panel-header-left">
          <Button
            type="default"
            icon={<MenuOutlined />}
            onClick={() => setMobileMenuVisible(true)}
            aria-label="Menüyü aç"
            className={styles.panelHeaderMenuBtn}
          >
            <span className={styles.panelHeaderMenuLabel}>Menü</span>
          </Button>
          <Button type="text" icon={<HomeOutlined />} onClick={() => navigate('/')} className={styles.panelHeaderHomeBtn}>
            Anasayfa
          </Button>
        </div>
        <div className="panel-header-right">
          <span className={styles.panelHeaderTitle}>Panel</span>
        </div>
      </header>

      <div className={styles.profileWrapper}>
        <Layout className={styles.profileLayout}>
          <Sider
            width={260}
            className={`${styles.sidebar} panel-sider-dark`}
            breakpoint="lg"
            collapsedWidth={0}
            trigger={null}
            collapsed={false}
            style={{ display: 'block' }}
          >
            {renderSidebar()}
          </Sider>
          <Layout className={styles.contentLayout}>
            <Content className={styles.contentArea}>
              {selectedMenu === 'profile' && (
                <SubscriptionProtectedRoute allowUnsubscribed={true}>
                  <ProfileTab />
                </SubscriptionProtectedRoute>
              )}
              {selectedMenu === 'subscription' && (
                <SubscriptionProtectedRoute allowUnsubscribed={true}>
                  <SubscriptionTab />
                </SubscriptionProtectedRoute>
              )}
              {selectedMenu === 'settings' && (
                <SubscriptionProtectedRoute allowUnsubscribed={true}>
                  <SettingsTab />
                </SubscriptionProtectedRoute>
              )}
              {selectedMenu === 'education' && (
                <SubscriptionProtectedRoute>
                  <EducationTab />
                </SubscriptionProtectedRoute>
              )}
              {selectedMenu === 'projects' && (
                <SubscriptionProtectedRoute>
                  <ProjectsTab />
                </SubscriptionProtectedRoute>
              )}
              {selectedMenu === 'skills' && (
                <SubscriptionProtectedRoute>
                  <SkillsTab />
                </SubscriptionProtectedRoute>
              )}
              {selectedMenu === 'work' && (
                <SubscriptionProtectedRoute>
                  <WorkExperienceTab />
                </SubscriptionProtectedRoute>
              )}
              {selectedMenu === 'certificates' && (
                <SubscriptionProtectedRoute>
                  <CertificateTab />
                </SubscriptionProtectedRoute>
              )}
              {selectedMenu === 'job-applications' && (
                <SubscriptionProtectedRoute>
                  <JobApplicationsTab />
                </SubscriptionProtectedRoute>
              )}
              {selectedMenu === 'interviews' && (
                <SubscriptionProtectedRoute>
                  <InterviewsTab />
                </SubscriptionProtectedRoute>
              )}
              {selectedMenu === 'bootcamps' && (
                <SubscriptionProtectedRoute>
                  <BootcampsTab />
                </SubscriptionProtectedRoute>
              )}
              {selectedMenu === 'services' && (
                <SubscriptionProtectedRoute>
                  <ServicesTab />
                </SubscriptionProtectedRoute>
              )}
              {selectedMenu === 'mentees' && (
                <SubscriptionProtectedRoute>
                  <MenteesTab />
                </SubscriptionProtectedRoute>
              )}
              {selectedMenu === 'referanslar' && (
                <SubscriptionProtectedRoute>
                  <ReferanslarTab />
                </SubscriptionProtectedRoute>
              )}
              {selectedMenu === 'advertisements' && (
                <SubscriptionProtectedRoute>
                  <AdvertisementTab />
                </SubscriptionProtectedRoute>
              )}
              {selectedMenu === 'availability' && (
                <SubscriptionProtectedRoute>
                  <AvailabilityTab />
                </SubscriptionProtectedRoute>
              )}
              {selectedMenu === 'appointments' && (
                <SubscriptionProtectedRoute>
                  <AppointmentsTab />
                </SubscriptionProtectedRoute>
              )}
          </Content>
        </Layout>
        </Layout>
      </div>

      {/* Mobile Drawer */}
      <Drawer
        title="Menü"
        placement="left"
        onClose={() => setMobileMenuVisible(false)}
        open={mobileMenuVisible}
        width={260}
        styles={{ header: { borderBottom: '1px solid rgba(255,255,255,0.08)', color: '#f8fafc', background: '#1e293b' }, body: { padding: 0, background: '#1e293b' } }}
        maskClosable
        closable
        className={styles.mobileDrawer}
      >
        <div style={{ padding: '24px 0', height: '100%', display: 'flex', flexDirection: 'column' }}>
          {renderSidebar()}
        </div>
      </Drawer>
    </Layout>
  );
};

export default MentorPanel;
